import React, { useMemo, useState, useRef, useCallback, useEffect } from 'react';
import {
  Paper,
  Stack,
  Typography,
  Box,
  InputAdornment,
  Divider,
  Button,
  TextField,
  styled,
  Popper,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  Alert,
  Tooltip,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Search,
  Link as LinkIcon,
} from '@mui/icons-material';
import Grid from '@mui/material/Unstable_Grid2';
import { DirectionalBiasSlider } from '@/shared/fields/Sliders';
import { NumericFormatCustom } from '@/shared/fields/NumberFieldFormat';
import { TokenIcon, ExchangeIcon } from '@/shared/components/Icons';
import { isolatedHolographicStyles } from '@/theme/holographicEffects';
import { debounce } from 'lodash';
import AutoSizer from 'react-virtualized-auto-sizer';
import { VariableSizeList as List } from 'react-window';
import { formatQty, msAndKs } from '@/util';
import { useLeverage, LeverageChip } from '@/shared/components/LeverageChip';
import { useAccountBalanceContext } from '@/pages/dashboard/orderEntry/AccountBalanceContext';
import { useOrderForm } from '@/shared/context/OrderFormProvider';
import useViewport from '@/shared/hooks/useViewport';
import useMarketMakerForm from '../hooks/useMarketMakerForm';
import MarketMakerPreTradeAnalytics from './MarketMakerPreTradeAnalytics';
import MarketMakerConfiguration from './MarketMakerConfiguration';
import LifetimeStats from './LifetimeStats';

const HoloPairRow = styled(Stack)(({ theme }) => ({
  ...isolatedHolographicStyles(theme),
  margin: 0,
  borderRadius: 0,
  overflow: 'hidden',
  '&:hover': {
    ...isolatedHolographicStyles(theme)['&:hover'],
    backgroundColor: 'text.offBlack',
  },
  '&::before': {
    ...isolatedHolographicStyles(theme)['&::before'],
    transition: 'transform 0.6s ease 0.1s',
    borderRadius: 0,
  },
  '&::after': {
    ...isolatedHolographicStyles(theme)['&::after'],
    transition: 'opacity 0.3s ease 0.1s',
    borderRadius: 0,
  },
  '&:hover::before': {
    transform: 'translateX(200%)',
  },
  '&:hover::after': {
    opacity: 0.12,
    animation: 'holographic-shimmer 5s ease-in-out 0.1s forwards',
  },
}));

function PairRow({ index, style, data }) {
  const { pairs, onSelectPair } = data;
  const pair = pairs[index];
  const volumeNotional = pair?.ticker?.volume24hNotional;
  const lastPrice = pair?.ticker?.lastPrice;

  return (
    <Box style={{ ...style, borderRadius: 0, overflow: 'hidden', margin: 0 }}>
      <HoloPairRow
        alignItems='center'
        direction='row'
        justifyContent='space-between'
        sx={{
          cursor: 'pointer',
          height: 'inherit',
          px: 3.75,
        }}
        onClick={() => onSelectPair(pair.id)}
      >
        <Stack alignItems='center' direction='row' spacing={1}>
          <TokenIcon style={{ height: '34px', width: '34px' }} tokenName={pair.id} />
          <Stack direction='column' spacing={0.5}>
            <Typography
              sx={{
                maxWidth: 220,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
              variant='body1'
            >
              {pair.id}
            </Typography>
            <Typography color='text.secondary' variant='body2'>
              {formatQty(lastPrice, true)}
            </Typography>
          </Stack>
        </Stack>

        <Stack alignItems='flex-end' direction='column' spacing={0}>
          <Typography color='text.secondary' variant='body2'>
            24h Volume
          </Typography>
          <Typography variant='body2'>
            <span>$</span>
            {msAndKs(volumeNotional)}
          </Typography>
        </Stack>
      </HoloPairRow>
    </Box>
  );
}

function MarketMakerPairSelector({ pair, pairs, onSelectPair, onOpenChange }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [confirmedSearchQuery, setConfirmedSearchQuery] = useState('');
  const popperRef = useRef(null);
  const triggerRef = useRef(null);

  const open = Boolean(anchorEl);
  const dropdownWidth = triggerRef.current ? triggerRef.current.offsetWidth : undefined;

  const handleOpen = useCallback(
    (e) => {
      if (anchorEl) {
        setAnchorEl(null);
        return;
      }
      setAnchorEl(e.currentTarget);
    },
    [anchorEl]
  );

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSelectPair = (pairId) => {
    onSelectPair(pairId);
    handleClose();
  };

  const debouncedHandleSearchChange = useCallback(
    debounce((event) => {
      setConfirmedSearchQuery(event.target.value);
    }, 1000),
    []
  );

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
    debouncedHandleSearchChange(event);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        popperRef.current &&
        !popperRef.current.contains(event.target) &&
        !triggerRef.current.contains(event.target)
      ) {
        handleClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [popperRef]);

  useEffect(() => {
    if (open) {
      onOpenChange(open);
    }
  }, [open]);

  const filteredPairs = useMemo(() => {
    let tradingPairs = [...pairs];

    if (confirmedSearchQuery) {
      tradingPairs = tradingPairs.filter((p) => p.id.toLowerCase().includes(confirmedSearchQuery.toLowerCase()));
    }
    return tradingPairs;
  }, [pairs, confirmedSearchQuery]);

  return (
    <>
      <Stack
        alignItems='center'
        direction='row'
        ref={triggerRef}
        spacing={1}
        sx={{
          p: 3,
          borderRadius: '50px',
          cursor: 'pointer',
          '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.05)' },
        }}
        onClick={handleOpen}
      >
        <TokenIcon style={{ width: 24, height: 24 }} tokenName={pair} />
        <Typography variant='subtitle1'>{pair || 'Select Pair'}</Typography>
        <ExpandMoreIcon />
      </Stack>
      <Popper
        anchorEl={anchorEl}
        open={open}
        placement='bottom'
        ref={popperRef}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
        }}
        onClose={handleClose}
      >
        <Paper
          elevation={0}
          sx={{
            width: dropdownWidth,
            backgroundColor: `background.container40`, // 25% opacity - much more transparent
            backdropFilter: 'blur(15px)',
            borderRadius: 0,
            border: 'none',
          }}
        >
          <Stack direction='column'>
            <Box sx={{ p: 3 }}>
              <TextField
                fullWidth
                autoComplete='off'
                InputProps={{
                  startAdornment: (
                    <InputAdornment position='start'>
                      <Search sx={{ color: 'grey' }} />
                    </InputAdornment>
                  ),
                }}
                placeholder='Search'
                size='small'
                value={searchQuery}
                variant='outlined'
                onChange={handleSearchChange}
              />
            </Box>
            <Divider />
            <Box sx={{ position: 'relative', height: '500px', py: 0 }}>
              <AutoSizer>
                {({ height, width }) => (
                  <List
                    height={height}
                    itemCount={filteredPairs.length}
                    itemData={{ pairs: filteredPairs, onSelectPair: handleSelectPair }}
                    itemSize={() => 60}
                    width={width}
                  >
                    {PairRow}
                  </List>
                )}
              </AutoSizer>
            </Box>
          </Stack>
        </Paper>
      </Popper>
    </>
  );
}

function MarketMaketAccountSelector({ account, onSelectAccount, accounts }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const popperRef = useRef(null);
  const triggerRef = useRef(null); // keeps inner content ref if needed
  const containerRef = useRef(null); // the visible Select Account field
  const open = Boolean(anchorEl);

  const handleOpen = useCallback(() => {
    if (anchorEl) {
      setAnchorEl(null);
      return;
    }
    setAnchorEl(containerRef.current);
  }, [anchorEl]);

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSelectAccount = (a) => {
    onSelectAccount(a);
    handleClose();
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        popperRef.current &&
        !popperRef.current.contains(event.target) &&
        !triggerRef.current.contains(event.target)
      ) {
        handleClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [popperRef]);

  const dropdownWidth = containerRef.current ? containerRef.current.offsetWidth : undefined;

  return (
    <Paper
      elevation={1}
      ref={containerRef}
      sx={{
        p: 3,
        pr: 4,
        cursor: 'pointer',
        width: '96%',
        maxWidth: '96%',
        overflow: 'hidden',
        '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.05)' },
        backgroundColor: 'rgba(10, 12, 24, 0.45)',
        backdropFilter: 'blur(15px)',
      }}
      onClick={handleOpen}
    >
      <Stack alignItems='center' direction='row' ref={triggerRef} spacing={1} sx={{ width: '100%', maxWidth: '100%' }}>
        {account ? (
          <>
            <ExchangeIcon exchangeName={account.exchangeName} style={{ height: '25px', width: '25px' }} />
            <Typography
              sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}
              variant='body1'
            >
              {account.name}
            </Typography>
          </>
        ) : (
          <Box sx={{ height: '25px', display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
            <Typography textAlign='center' variant='subtitle2'>
              Select Account
            </Typography>
          </Box>
        )}
      </Stack>
      <Popper
        anchorEl={anchorEl}
        open={open}
        placement='bottom-start'
        ref={popperRef}
        sx={{
          zIndex: 9999,
        }}
      >
        <Paper
          elevation={0}
          sx={{
            width: dropdownWidth,
            backgroundColor: `background.container40`, // 25% opacity - much more transparent
            backdropFilter: 'blur(15px)',
            borderRadius: 0,
            border: 'none',
          }}
        >
          <Stack direction='column'>
            {Object.values(accounts).map((a) => (
              <HoloPairRow
                alignItems='center'
                direction='row'
                key={a.id}
                spacing={1.5}
                sx={{
                  cursor: 'pointer',
                  height: '40px',
                  px: 3.75,
                }}
                onClick={() => handleSelectAccount(a)}
              >
                <ExchangeIcon exchangeName={a.exchangeName} style={{ height: '25px', width: '25px' }} />
                <Typography
                  sx={{
                    maxWidth: 220,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                  variant='body1'
                >
                  {a.name}
                </Typography>
              </HoloPairRow>
            ))}
          </Stack>
        </Paper>
      </Popper>
    </Paper>
  );
}

function MarketMakerModeSelector({ mode, setMode, tradeableModes }) {
  const displayDuration = (duration) => {
    if (!duration) return '-';

    const hours = Math.floor(duration / 60);
    const minutes = duration % 60;
    const minuteStr = minutes === 1 ? 'minute' : 'minutes';

    if (hours === 0) {
      return `${minutes} ${minuteStr}`;
    }

    return `${hours} hours ${minutes} ${minuteStr}`;
  };

  return (
    <ToggleButtonGroup
      exclusive
      fullWidth
      size='small'
      sx={{ width: '100%' }}
      value={mode}
      onChange={(_, value) => value && setMode(value)}
    >
      {tradeableModes.map((m) => (
        <ToggleButton
          key={m.value}
          sx={{
            color: `orderUrgency.${m.urgency}`,
            flex: 1,
            '&.Mui-selected': {
              backgroundColor: 'orderUrgency.background',
              color: `orderUrgency.${m.urgency}`,
            },
          }}
          value={m.value}
        >
          <Stack alignItems='center' direction='column' spacing={0.5}>
            <Typography color='inherit' fontWeight='bold' variant='body2'>
              {m.label}
            </Typography>
            <Typography color='text.secondary' sx={{ fontSize: '0.65rem' }} variant='body2'>
              {m.subtitle}
            </Typography>
            <Typography color='text.secondary' variant='body2'>
              {displayDuration(m.duration)}
            </Typography>
          </Stack>
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  );
}

export default function MarketMakerEntryForm({ lifetime, activeOrders = [] }) {
  const { isMobile } = useViewport();
  const { initialLoadValue } = useOrderForm();
  const {
    feeAmount,
    setFeeAmount,
    volumeAmount,
    setVolumeAmount,
    handleFeeBlur,
    handleVolumeBlur,
    isSyncing,
    activeField,
    derivedNotional,
    account,
    setAccount,
    pair,
    setPair,
    mode,
    setMode,
    directionalBias,
    setDirectionalBias,
    derivedDurationMinutes,
    derivedPassiveness,
    handleSubmit,
    isSubmitting,
    canSubmit,
    tradeablePairs,
    tradableAccounts,
    tradeableModes,
  } = useMarketMakerForm();

  const { calculateMarginBalance, calculateAssetBalance } = useAccountBalanceContext();

  const currentPair = useMemo(() => tradeablePairs.find((p) => p.id === pair), [tradeablePairs, pair]);
  const { leverage, adjustLeverage, maxLeverage } = useLeverage({ pair: currentPair, account });
  const quoteSymbol = useMemo(() => {
    if (currentPair?.quote) return currentPair.quote;
    const id = currentPair?.id || '';
    const lastSep = Math.max(id.lastIndexOf('-'), id.lastIndexOf(':'));
    if (lastSep >= 0) return id.substring(lastSep + 1);
    return 'USDT';
  }, [currentPair]);

  const availableMargin = useMemo(() => {
    try {
      if (!account || !currentPair || !initialLoadValue?.accounts) return null;
      const marginBal = calculateMarginBalance(quoteSymbol, {
        selectedAccounts: [account.name],
        selectedPair: currentPair,
        accounts: initialLoadValue.accounts,
      });
      if (marginBal && marginBal > 0) return marginBal;
      // Fallback to raw asset balance if margin-specific balance is unavailable
      const assetBal = calculateAssetBalance(quoteSymbol, {
        selectedAccounts: [account.name],
        selectedPair: currentPair,
        accounts: initialLoadValue.accounts,
      });
      return assetBal || 0;
    } catch (_) {
      return null;
    }
  }, [account, currentPair, quoteSymbol, calculateMarginBalance, initialLoadValue]);

  const showSpotWarning = useMemo(() => {
    return Boolean(currentPair && currentPair.market_type === 'spot');
  }, [currentPair]);

  const showBudgetWarning = useMemo(() => {
    if (!feeAmount || availableMargin == null) return false;
    const numericFee = parseFloat(String(feeAmount).replace(/,/g, ''));
    return numericFee > availableMargin;
  }, [feeAmount, availableMargin]);

  // Calculate recommended margin (same logic as in analytics component)
  const recommendedMargin = useMemo(() => {
    const numericVolume = parseFloat(String(volumeAmount).replace(/,/g, ''));
    if (!numericVolume || !currentPair) return null;

    let baseMargin;

    // Spot: exposure * notional target
    if (currentPair.market_type === 'spot') {
      const exposureTolerance = 0.07;
      baseMargin = numericVolume * exposureTolerance;
    } else {
      // Derivatives: notional / leverage with safety buffer
      if (!leverage?.leverage) return null;

      // Safety buffer based on mode
      const safetyBufferByMode = {
        aggressive: 2.0,
        normal: 1.0,
        passive: 0.5,
      };
      const safetyBuffer = safetyBufferByMode[mode] || 1.0;

      baseMargin = (numericVolume / leverage.leverage) * safetyBuffer;
    }

    // Apply directional bias adjustment: up to 20% more margin for extreme bias
    // At -1 or 1 bias: 20% more margin, at 0 bias: no change
    const biasAdjustment = Math.abs(directionalBias) * 0.2; // 0 to 0.2 multiplier
    const adjustedMargin = baseMargin * (1 + biasAdjustment);

    return adjustedMargin;
  }, [volumeAmount, currentPair, leverage, mode, directionalBias]);

  // Check if available margin is critically low (< 10% of recommended)
  const showMarginWarning = useMemo(() => {
    if (!recommendedMargin || availableMargin == null) return false;
    return availableMargin < recommendedMargin * 0.1;
  }, [recommendedMargin, availableMargin]);

  // Check if there's an active order with the same account and symbol
  const showDuplicateWarning = useMemo(() => {
    if (!account || !pair || !Array.isArray(activeOrders)) return false;

    const activeStatuses = ['ACTIVE', 'RUNNING', 'PAUSED', 'FINISHER', 'PENDING'];

    return activeOrders.some((order) => {
      // Check if order is active
      if (!activeStatuses.includes(order.status)) return false;

      // Check if same account
      const orderAccounts = order.accounts || order.account_names || [];
      const hasMatchingAccount = orderAccounts.includes(account.name);

      // Check if same symbol (pair)
      const orderPairs = (order.pairs || '').split(',').map((p) => p.trim());
      const hasMatchingPair = orderPairs.includes(pair);

      return hasMatchingAccount && hasMatchingPair;
    });
  }, [account, pair, activeOrders]);

  const [summaryOpen, setSummaryOpen] = useState(false);

  if (isMobile) {
    return (
      <>
        <Stack direction='column' spacing={2} sx={{ width: '100%', pb: 28 }}>
          {/* Account Selection */}
          <Stack direction='column' spacing={1}>
            <Typography variant='small1'>Account</Typography>
            <MarketMaketAccountSelector account={account} accounts={tradableAccounts} onSelectAccount={setAccount} />
          </Stack>

          {/* Budget & Volume Row */}
          <Stack direction='column' spacing={1}>
            <Stack alignItems='center' direction='row' spacing={2}>
              <Typography sx={{ flex: 1 }} variant='small1'>
                Budget
              </Typography>
              <LinkIcon sx={{ color: 'text.secondary' }} />
              <Typography sx={{ flex: 1, textAlign: 'right' }} variant='small1'>
                Volume
              </Typography>
            </Stack>
            <Stack alignItems='center' direction='row' spacing={2}>
              <Paper
                elevation={1}
                sx={{ p: 2, minHeight: 56, display: 'flex', alignItems: 'center', position: 'relative', flex: 1 }}
              >
                {isSyncing && activeField === 'volume' && (
                  <Box
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      backgroundColor: 'rgba(0,0,0,0.1)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      zIndex: 1,
                    }}
                  >
                    <CircularProgress size={20} />
                  </Box>
                )}
                <TextField
                  fullWidth
                  autoComplete='off'
                  disabled={isSyncing && activeField === 'volume'}
                  InputProps={{
                    disableUnderline: true,
                    inputComponent: NumericFormatCustom,
                    sx: { input: { textAlign: 'left', fontSize: '1.25rem', lineHeight: '1.75rem', fontWeight: 400 } },
                    startAdornment: (
                      <InputAdornment position='start'>
                        <Typography color='grey' variant='h3'>
                          $
                        </Typography>
                      </InputAdornment>
                    ),
                  }}
                  placeholder='0'
                  value={feeAmount}
                  variant='standard'
                  onBlur={handleFeeBlur}
                  onChange={(e) => setFeeAmount(e.target.value)}
                />
              </Paper>

              <Paper
                elevation={1}
                sx={{ p: 2, minHeight: 56, display: 'flex', alignItems: 'center', position: 'relative', flex: 1 }}
              >
                {isSyncing && activeField === 'fee' && (
                  <Box
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      backgroundColor: 'rgba(0,0,0,0.1)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      zIndex: 1,
                    }}
                  >
                    <CircularProgress size={20} />
                  </Box>
                )}
                <TextField
                  fullWidth
                  autoComplete='off'
                  disabled={isSyncing && activeField === 'fee'}
                  InputProps={{
                    disableUnderline: true,
                    inputComponent: NumericFormatCustom,
                    sx: { input: { textAlign: 'left', fontSize: '1.25rem', lineHeight: '1.75rem', fontWeight: 400 } },
                    startAdornment: (
                      <InputAdornment position='start'>
                        <Typography color='grey' variant='h3'>
                          $
                        </Typography>
                      </InputAdornment>
                    ),
                  }}
                  placeholder='0'
                  value={volumeAmount}
                  variant='standard'
                  onBlur={handleVolumeBlur}
                  onChange={(e) => setVolumeAmount(e.target.value)}
                />
              </Paper>
            </Stack>
          </Stack>

          {/* Pair & Leverage */}
          <Stack direction='column' spacing={1}>
            <Typography variant='small1'>Pair & Leverage</Typography>
            <Paper
              elevation={1}
              sx={{
                p: 2,
                minHeight: 56,
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <Stack
                alignItems='center'
                direction='row'
                spacing={1}
                sx={{ justifyContent: 'space-between', width: '100%' }}
              >
                <Box sx={{ flex: 1, pr: 2 }}>
                  <MarketMakerPairSelector
                    pair={pair}
                    pairs={tradeablePairs}
                    onOpenChange={() => {}}
                    onSelectPair={setPair}
                  />
                </Box>
                <Box sx={{ width: 64 }}>
                  <LeverageChip
                    adjustLeverage={adjustLeverage}
                    disabled={!account || !currentPair || currentPair.market_type === 'spot'}
                    exchange={leverage?.exchange}
                    leverage={leverage?.leverage}
                    maxLeverage={maxLeverage}
                    pairId={pair}
                  />
                </Box>
              </Stack>
            </Paper>
          </Stack>

          {/* Strategy & Bias */}
          <MarketMakerModeSelector mode={mode} setMode={setMode} tradeableModes={tradeableModes} />
          <DirectionalBiasSlider directionalBias={directionalBias} setDirectionalBias={setDirectionalBias} />
          {/* Warnings */}
          {showMarginWarning && (
            <Alert
              severity='error'
              sx={{ borderRadius: 1, border: '1px solid', borderColor: 'error.main' }}
              variant='outlined'
            >
              <Typography variant='body2'>
                Insufficient margin: Available margin is less than 10% of recommended. Please choose a smaller budget,
                increase leverage, or add more margin.
              </Typography>
            </Alert>
          )}
          {showSpotWarning && (
            <Alert
              icon={false}
              severity='warning'
              sx={{ borderRadius: 1, border: '1px solid', borderColor: 'warning.main' }}
              variant='outlined'
            >
              <Typography variant='body2'>
                For SPOT pairs, ensure you hold at least half of the recommended margin in the SPOT token.
              </Typography>
            </Alert>
          )}
          {showDuplicateWarning && (
            <Alert
              icon={false}
              severity='warning'
              sx={{ borderRadius: 1, border: '1px solid', borderColor: 'warning.main' }}
              variant='outlined'
            >
              <Typography variant='body2'>
                Running the bot on the same symbol with the same account may result in unexpected exposure and canceled
                orders.
              </Typography>
            </Alert>
          )}
        </Stack>

        {/* Combined Summary + CTA container */}
        <Box sx={{ position: 'fixed', left: 16, right: 16, bottom: 4, zIndex: 1000 }}>
          <Paper elevation={3} sx={{ borderRadius: 1, backgroundColor: 'background.container' }}>
            <Stack direction='column' spacing={1} sx={{ p: 1.5 }}>
              <Stack
                alignItems='center'
                direction='row'
                justifyContent='space-between'
                sx={{ px: 1, py: 0.5, cursor: 'pointer' }}
                onClick={() => setSummaryOpen((v) => !v)}
              >
                <Typography variant='subtitle2'>Summary</Typography>
                {summaryOpen ? <ExpandMoreIcon /> : <ExpandLessIcon />}
              </Stack>
              {summaryOpen && (
                <Box sx={{ px: 1 }}>
                  <Stack
                    direction='column'
                    spacing={2}
                    sx={{
                      maxHeight: '45vh',
                      overflow: 'auto',
                      scrollbarWidth: 'none', // Firefox
                      msOverflowStyle: 'none', // IE/Edge
                      '&::-webkit-scrollbar': { display: 'none' }, // WebKit
                    }}
                  >
                    <MarketMakerPreTradeAnalytics
                      amount={derivedNotional}
                      availableMargin={availableMargin}
                      directionalBias={directionalBias}
                      estimatedFees={feeAmount}
                      exposureTolerance={0.07}
                      isLoading={isSyncing && (activeField === 'fee' || activeField === 'volume')}
                      isSpot={Boolean(currentPair && currentPair.market_type === 'spot')}
                      leverage={leverage}
                      mode={mode}
                    />
                    <MarketMakerConfiguration
                      durationMinutes={derivedDurationMinutes}
                      passiveness={derivedPassiveness}
                    />
                    <LifetimeStats lifetime={lifetime} />
                  </Stack>
                </Box>
              )}
              <Button
                fullWidth
                color='primary'
                disabled={isSubmitting || !canSubmit}
                sx={{ height: 48, mt: 0 }}
                variant='contained'
                onClick={handleSubmit}
              >
                {isSubmitting ? <CircularProgress size={30} /> : 'Start Trading'}
              </Button>
            </Stack>
          </Paper>
        </Box>
      </>
    );
  }

  return (
    <Stack direction='row' spacing={4} sx={{ width: '100%' }}>
      {/* Left Section - Trading Controls */}
      <Stack direction='column' spacing={3} sx={{ flex: 7, minWidth: 0 }}>
        {/* Account Selection */}
        <Stack direction='column' spacing={1}>
          <Typography variant='small1'>Account</Typography>
          <MarketMaketAccountSelector account={account} accounts={tradableAccounts} onSelectAccount={setAccount} />
        </Stack>

        {/* Trading Parameters Row - Fee | Volume | Pair */}
        <Stack direction='row' spacing={3} sx={{ width: '100%', alignItems: 'flex-end' }}>
          {/* Fee Budget */}
          <Stack direction='column' spacing={1} sx={{ flex: 1 }}>
            <Tooltip
              arrow
              title={
                showBudgetWarning ? `Budget exceeds available margin ($${availableMargin?.toFixed(2) || '0.00'})` : ''
              }
            >
              <Typography color={showBudgetWarning ? 'error.main' : 'text.primary'} variant='small1'>
                Budget
              </Typography>
            </Tooltip>
            <Paper
              elevation={1}
              sx={{ p: 2, minHeight: 56, display: 'flex', alignItems: 'center', position: 'relative' }}
            >
              {isSyncing && activeField === 'volume' && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.1)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1,
                  }}
                >
                  <CircularProgress size={20} />
                </Box>
              )}
              <TextField
                fullWidth
                autoComplete='off'
                disabled={isSyncing && activeField === 'volume'}
                InputProps={{
                  disableUnderline: true,
                  inputComponent: NumericFormatCustom,
                  sx: {
                    input: {
                      textAlign: 'left',
                      fontSize: '1.5rem',
                      lineHeight: '1.875rem',
                      fontWeight: 400,
                      color: showBudgetWarning ? 'error.main' : 'inherit',
                    },
                  },
                  startAdornment: (
                    <InputAdornment position='start'>
                      <Typography color='grey' variant='h3'>
                        $
                      </Typography>
                    </InputAdornment>
                  ),
                }}
                placeholder='0'
                value={feeAmount}
                variant='standard'
                onBlur={handleFeeBlur}
                onChange={(e) => setFeeAmount(e.target.value)}
              />
            </Paper>
          </Stack>

          {/* Link Icon */}
          <Box sx={{ pb: '28px', display: 'flex', alignItems: 'center' }}>
            <Tooltip arrow title='Budget and Volume are linked based on estimated trading fees'>
              <LinkIcon sx={{ color: 'text.secondary', fontSize: 28 }} />
            </Tooltip>
          </Box>

          {/* Volume */}
          <Stack direction='column' spacing={1} sx={{ flex: 1 }}>
            <Typography variant='small1'>Volume</Typography>
            <Paper
              elevation={1}
              sx={{ p: 2, minHeight: 56, display: 'flex', alignItems: 'center', position: 'relative' }}
            >
              {isSyncing && activeField === 'fee' && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.1)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1,
                  }}
                >
                  <CircularProgress size={20} />
                </Box>
              )}
              <TextField
                fullWidth
                autoComplete='off'
                disabled={isSyncing && activeField === 'fee'}
                InputProps={{
                  disableUnderline: true,
                  inputComponent: NumericFormatCustom,
                  sx: {
                    input: {
                      textAlign: 'left',
                      fontSize: '1.5rem',
                      lineHeight: '1.875rem',
                      fontWeight: 400,
                    },
                  },
                  startAdornment: (
                    <InputAdornment position='start'>
                      <Typography color='grey' variant='h3'>
                        $
                      </Typography>
                    </InputAdornment>
                  ),
                }}
                placeholder='0'
                value={volumeAmount}
                variant='standard'
                onBlur={handleVolumeBlur}
                onChange={(e) => setVolumeAmount(e.target.value)}
              />
            </Paper>
          </Stack>

          {/* Pair & Leverage */}
          <Stack direction='column' spacing={1} sx={{ flex: 2 }}>
            <Typography variant='small1'>Pair & Leverage</Typography>
            <Paper elevation={1} sx={{ p: 2, minHeight: 56, display: 'flex', alignItems: 'center' }}>
              <Stack
                alignItems='center'
                direction='row'
                spacing={1}
                sx={{ justifyContent: 'space-between', width: '100%' }}
              >
                <Box sx={{ flex: 1, pr: 2 }}>
                  <MarketMakerPairSelector
                    pair={pair}
                    pairs={tradeablePairs}
                    onOpenChange={() => {}}
                    onSelectPair={setPair}
                  />
                </Box>
                <Box sx={{ width: 64 }}>
                  <LeverageChip
                    adjustLeverage={adjustLeverage}
                    disabled={!account || !currentPair || currentPair.market_type === 'spot'}
                    exchange={leverage?.exchange}
                    leverage={leverage?.leverage}
                    maxLeverage={maxLeverage}
                    pairId={pair}
                  />
                </Box>
              </Stack>
            </Paper>
          </Stack>
        </Stack>

        {/* Strategy Selection */}
        <MarketMakerModeSelector mode={mode} setMode={setMode} tradeableModes={tradeableModes} />

        {/* Directional Bias */}
        <DirectionalBiasSlider directionalBias={directionalBias} setDirectionalBias={setDirectionalBias} />

        {/* Start Trading Button */}
        <Button
          fullWidth
          color='primary'
          disabled={isSubmitting || !canSubmit}
          sx={{ height: 48 }}
          variant='contained'
          onClick={handleSubmit}
        >
          {isSubmitting ? <CircularProgress size={30} /> : 'Start Trading'}
        </Button>

        {/* Warnings under the submit button */}
        {showMarginWarning && (
          <Alert
            severity='error'
            sx={{ borderRadius: 1, border: '1px solid', borderColor: 'error.main' }}
            variant='outlined'
          >
            <Typography variant='body2'>
              Insufficient margin: Available margin is less than 10% of recommended. Please choose a smaller budget,
              increase leverage, or add more margin.
            </Typography>
          </Alert>
        )}

        {showSpotWarning && (
          <Alert
            icon={false}
            severity='warning'
            sx={{ borderRadius: 1, border: '1px solid', borderColor: 'warning.main' }}
            variant='outlined'
          >
            <Typography variant='body2'>
              For SPOT pairs, ensure you hold at least half of the recommended margin in the SPOT token.
            </Typography>
          </Alert>
        )}

        {showDuplicateWarning && (
          <Alert
            icon={false}
            severity='warning'
            sx={{ borderRadius: 1, border: '1px solid', borderColor: 'warning.main' }}
            variant='outlined'
          >
            <Typography variant='body2'>
              Running the bot on the same symbol with the same account may result in unexpected exposure and canceled
              orders.
            </Typography>
          </Alert>
        )}
      </Stack>

      {/* Right Section - Analytics and Configuration */}
      <Stack direction='column' spacing={3} sx={{ flex: 3, minWidth: 0 }}>
        <MarketMakerPreTradeAnalytics
          amount={derivedNotional}
          availableMargin={availableMargin}
          directionalBias={directionalBias}
          estimatedFees={feeAmount}
          exposureTolerance={0.07}
          isLoading={isSyncing && (activeField === 'fee' || activeField === 'volume')}
          isSpot={Boolean(currentPair && currentPair.market_type === 'spot')}
          leverage={leverage}
          mode={mode}
        />

        <MarketMakerConfiguration durationMinutes={derivedDurationMinutes} passiveness={derivedPassiveness} />

        <Divider />

        <LifetimeStats lifetime={lifetime} />
      </Stack>
    </Stack>
  );
}
